import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { Modal } from '../../components';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { getSupplierCatalogueFit } from '../../util/api';
import { tpmsRequirementText } from '../../util/wheelFitment';
import { RegNumInput } from '../WP/RegNumInput';

import css from './BuildAWheelSection.module.css';

/* ============================================================
 * "Bygg ditt hjul" — company profile build-a-wheel module.
 *
 * The reg-number lookup (RegNumInput → getLicensePlateInfo) feeds the
 * "Ditt fordon" box, the dimension toggle and the fitment chips. Before a
 * lookup, DEFAULT_VEHICLE (the mockup's Mercedes GLE) stands in as an
 * example so the module is not empty on first render.
 *
 * The products come from this dealer's SUPPLIER CATALOGUE — POST
 * /api/supplier-catalogue/fit, reading the SupplierCatalogItem collection
 * (P25009-426). They are not listings and never were: the catalogue holds
 * thousands of wholesaler articles that deliberately stay out of search,
 * the sitemap and the merchant feed. See docs/supplier-feed-handover.md §5.
 *
 * The catalogue is queried for the example car too, so what is shown is
 * always the dealer's real assortment. When the dealer has no catalogue the
 * module says so rather than showing invented products.
 *
 * SELLING IS NOT WIRED. Sharetribe transacts against a listing and a
 * catalogue row is not one, so "Lägg i varukorgen" is deliberately
 * disabled — that is Phase 2 and it is not designed yet.
 * ============================================================ */

// Inline SVGs reused from the mockup so the module is self-contained. Also the
// fallback when a supplier serves its pictures behind authentication, which
// Gummigrossen does — the API returns imageUrl: null for those.
const TYRE_SVG =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='150' height='170'><ellipse cx='75' cy='85' rx='55' ry='80' fill='%231e293b'/><ellipse cx='75' cy='85' rx='30' ry='46' fill='%23475569'/></svg>";
const RIM_SVG =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='170' height='170'><circle cx='85' cy='85' r='78' fill='%23c7ccd4'/><circle cx='85' cy='85' r='60' fill='%23aeb4be'/><circle cx='85' cy='85' r='16' fill='%23717784'/></svg>";

const SET = 4; // four wheels

/* The shop's own labour and parts, per wheel. These are NOT supplier data and
 * must not come from the catalogue: they are what this workshop charges to fit
 * the wheel, and the wholesaler has no opinion about them.
 *
 * The miljöavgift is missing from this list on purpose. It used to be a flat
 * MILJO = 25 here, charged on top of a tyre price that already contained the
 * supplier's own recycling fee — the same fee twice. The fee now comes from the
 * product (see the summary below), so there is one place it can be charged. */
const MONT = 329;
const VENTIL = 0;
const TPMS = 595;

/* The season toggle's labels, and the `tiretype` value each one selects. The
 * stored values are camelCase: a snake_case spelling silently matches nothing.
 * "Dubbara" (studdable, delivered unstudded) is mapped to friction by the
 * importer, so it needs no fourth button here. */
const SEASONS = [
  { label: 'Sommardäck', tiretype: 'summer' },
  { label: 'Dubbfria', tiretype: 'winterFriction' },
  { label: 'Dubbdäck', tiretype: 'winterDubbed' },
];

// Pre-lookup example so the module isn't empty on first render.
const DEFAULT_VEHICLE = {
  isExample: true,
  reg: 'ADT54X',
  name: 'Mercedes-Benz GLE (V167) 350 de 4-matic (167.106) 2023',
  make: 'Mercedes-Benz',
  model: 'GLE',
  year: '2023',
  bolts: 5,
  pcd: '112',
  cb: '66.5',
  et: '54 / 42',
  staggered: true,
  frontTires: ['275/55R19', '275/50R20', '275/45R21'],
  rearTires: ['315/40R21', '325/35R22', '285/40R22'],
  diameters: ['19', '20', '21', '22'],
};

const fmt = n => Number(n || 0).toLocaleString('sv-SE');

const uniqStrings = arr => [...new Set(arr.filter(Boolean).map(s => String(s).replaceAll('.00', '')))];

// A real tyre dimension looks like "235/60R18"; the API occasionally
// returns junk (e.g. "13175"). Filter so chips / staggered detection
// only ever see valid tyres.
const isTyre = s => /\d{2,3}\/\d{2}\s*r?\s*\d{2}/i.test(String(s));

// Strip a wheelsize rim string like "7.5Jx18 ET50.5" → "18 ET50.5".
const cleanRim = rim => (rim ? rim.replace(/^\d+(\.\d+)?Jx/i, '') : '');

const diametersFromTires = tires => {
  const set = new Set();
  tires.forEach(t => {
    const m = String(t).match(/R(\d{2})/i);
    if (m) set.add(m[1]);
  });
  return [...set].sort();
};

/**
 * The EU tyre label as the mockup writes it: "⛽ C · 🌧 A · 🔊 A (71 dB)".
 *
 * Built from the four separate facts the API returns rather than from a
 * pre-rendered string, so a label with only some of them stated prints the
 * parts it has instead of a sentence with holes in it.
 */
const euLabelText = euLabel => {
  if (!euLabel) return '';
  const parts = [];
  if (euLabel.fuel) parts.push(`⛽ ${euLabel.fuel}`);
  if (euLabel.wetGrip) parts.push(`🌧 ${euLabel.wetGrip}`);
  if (euLabel.noiseClass || euLabel.noiseDb) {
    const noise = [euLabel.noiseClass, euLabel.noiseDb ? `(${euLabel.noiseDb} dB)` : null]
      .filter(Boolean)
      .join(' ');
    parts.push(`🔊 ${noise}`);
  }
  return parts.join(' · ');
};

/* Normalise the licensePlateInfo response (two known API shapes,
 * `.result` or `.data[0]`) into the vehicle fields the module renders.
 * Mirrors the extraction logic in RegNumInput.handleRedirect / CarInfoCard. */
const extractVehicle = (info, reg) => {
  const car = info?.result || (Array.isArray(info?.data) ? info.data[0] : null);
  if (!car) return null;

  const brand = car.brand || car?.make?.name || '';
  const model =
    car?.series?.name || (typeof car?.series === 'string' ? car.series : '') || car?.model?.name || '';
  const motor = car?.name || car?.sales_name || '';
  // Only accept a real 4-digit model year; the API sometimes returns a
  // truncated value (e.g. "44") that shouldn't appear in the name.
  const year = String(car?.model_year || car?.end_year || '').match(/\b(?:19|20)\d{2}\b/)?.[0] || '';

  // Drop purely-numeric tokens (API sometimes leaks a type code like
  // "3446 44" into the model/motor) so the displayed name reads cleanly.
  const isJunkToken = t => /^[\d\s.]+$/.test(String(t).trim());
  const nameTokens = [brand, model, motor].filter(t => t && !isJunkToken(t));

  const bolts = car?.tire_info?.bolts ?? car?.technical?.stud_holes ?? '';
  const pcd = car?.tire_info?.bolt_circle ?? car?.technical?.pcd ?? '';
  const cb = car?.tire_info?.center_bore ?? car?.technical?.centre_bore ?? '';

  const frontTires = [];
  const rearTires = [];
  const frontRims = [];
  const wheelsArr = car?.tire_info?.tires || car?.wheels || [];
  wheelsArr.forEach(w => {
    if (w.front?.tire) frontTires.push(w.front.tire);
    if (w.rear?.tire) rearTires.push(w.rear.tire);
    if (w.front?.rim) frontRims.push(cleanRim(w.front.rim));
  });
  if (car?.original_tires) {
    if (car.original_tires.front_tires) frontTires.push(car.original_tires.front_tires);
    if (car.original_tires.rear_tires) rearTires.push(car.original_tires.rear_tires);
    if (car.original_tires.front_rims) frontRims.push(car.original_tires.front_rims);
  }

  const fTires = uniqStrings(frontTires).filter(isTyre);
  const rTires = uniqStrings(rearTires).filter(isTyre);
  const staggered = rTires.length > 0 && JSON.stringify(fTires) !== JSON.stringify(rTires);

  const frontRimStr = frontRims[0] || '';
  let et = frontRimStr.includes('ET') ? frontRimStr.split('ET')[1] : '';
  et = et ? et.replace(/[^\d.]/g, '') : '';

  const diameters = diametersFromTires([...fTires, ...rTires]);

  return {
    isExample: false,
    reg: (reg || '').replace(/\s/g, '').toUpperCase(),
    name: [...nameTokens, year].filter(Boolean).join(' '),
    brand,
    model,
    /* Kept as its own field, not only folded into `name`: the catalogue query
     * resolves the car's CarFitment row with make + model + year, and that row
     * is what carries the per-axle offset window. Without the year the lookup
     * falls back to the thinner plate-only match, which cannot check ET. */
    make: brand,
    year,
    bolts,
    pcd: pcd ? String(pcd) : '',
    cb: cb ? String(cb) : '',
    et,
    staggered,
    // P25009-489 BR-5 — "TPMS krävs · Sensor: …", '' when not needed or not known.
    tpmsText: tpmsRequirementText(info),
    frontTires: fTires,
    rearTires: rTires,
    diameters: diameters.length ? diameters : ['19', '20', '21', '22'],
  };
};

const diaDigits = d => String(d).replace(/\D/g, '');

// Tyre dimension for the chosen diameter — prefer rear (staggered) then front.
const tyreSizeForDiameter = (vehicle, diameter) => {
  const dia = diaDigits(diameter);
  const all = [...(vehicle.rearTires || []), ...(vehicle.frontTires || [])];
  return all.find(t => new RegExp(`R${dia}`, 'i').test(t)) || all[0] || '';
};

// Rim spec line derived from the vehicle (PCD / CB / ET) + chosen diameter.
const rimSpecForVehicle = (vehicle, diameter) => {
  const parts = [`${diaDigits(diameter)}"`];
  if (vehicle.bolts && vehicle.pcd) parts.push(`${vehicle.bolts}×${vehicle.pcd}`);
  if (vehicle.et) parts.push(`ET${vehicle.et}`);
  if (vehicle.cb) parts.push(`CB${vehicle.cb}`);
  return parts.join(' · ');
};

/* What the module sends the catalogue. Only the fitment facts — the display
 * name and the registration number are the page's business, not the query's. */
const catalogueVehicle = vehicle => ({
  make: vehicle.make || vehicle.brand || '',
  model: vehicle.model || '',
  year: vehicle.year || '',
  bolts: vehicle.bolts || '',
  boltCircle: vehicle.pcd || '',
  centerBore: vehicle.cb || '',
  frontTires: vehicle.frontTires || [],
  rearTires: vehicle.rearTires || [],
});

/**
 * Keep the shopper's choice across a refetch when the product is still offered.
 *
 * Changing the season refetches the tyres, and resetting the selection to the
 * first row every time would throw away a choice the shopper had just made
 * whenever an unrelated toggle moved.
 */
const keepOrFirst = (products, previous) => {
  if (!products || products.length === 0) return null;
  const stillThere = previous ? products.find(p => p.ref === previous.ref) : null;
  return stillThere || products[0];
};

const EMPTY_CATALOGUE = {
  tyres: [],
  rims: [],
  basis: null,
  etFiltered: false,
  /* Starts false, so before the first answer the module never claims the dealer
   * has stock it has not seen. */
  hasCatalogue: false,
};

const BuildAWheelSection = props => {
  const { displayName, profileUserId } = props;
  const dispatch = useDispatch();

  const [vehicle, setVehicle] = useState(DEFAULT_VEHICLE);
  const [carShown, setCarShown] = useState(true);
  /* P25009-443 BR-14: the phone collapse for the vehicle facts. Open by
   * default — a set value stays open (BR-8); the header carries the value
   * either way, so nothing is ever hidden behind a bare label. */
  const [carPanelOpen, setCarPanelOpen] = useState(true);
  const [regNum, setRegNum] = useState('');
  const [productType, setProductType] = useState('wheel');
  const [season, setSeason] = useState(SEASONS[0].label);
  const [diameter, setDiameter] = useState('21"');
  const [tpms, setTpms] = useState(false);

  const [catalogue, setCatalogue] = useState(EMPTY_CATALOGUE);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'

  const [selTyre, setSelTyre] = useState(null);
  const [selRim, setSelRim] = useState(null);

  const [picking, setPicking] = useState(null); // 'tire' | 'rim' | null
  const [brandFilter, setBrandFilter] = useState(null);

  /* P25009-406 AC-2 — a package page links here with the car in the query
   * string, so the builder opens on the car the buyer was already looking at
   * instead of an example. Read once on mount; a later reg lookup overrides it.
   *
   * The sizes are left empty on purpose: only the make and model arrive in the
   * URL, and the catalogue query below matches on those. Inventing dimensions
   * here would filter the assortment by a size nobody chose. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const make = params.get('make');
    const model = params.get('model');
    if (!make || !model) return;

    setVehicle(prev => ({
      ...prev,
      isExample: false,
      reg: '',
      make,
      model,
      name: `${make} ${model}`,
      frontTires: [],
      rearTires: [],
      diameters: [],
    }));
    setCarShown(true);
  }, []);

  const showTyre = productType === 'wheel' || productType === 'tire';
  const showRim = productType === 'wheel' || productType === 'rim';

  const seasonTiretype = (SEASONS.find(s => s.label === season) || SEASONS[0]).tiretype;

  /* The catalogue query. In an effect rather than during render because
   * util/api reads `window` to build its base URL, and this section is
   * server-rendered on the company profile. */
  useEffect(() => {
    if (!profileUserId) {
      setStatus('idle');
      setCatalogue(EMPTY_CATALOGUE);
      return undefined;
    }

    // A late response from a superseded query must not overwrite a newer one.
    let current = true;
    setStatus('loading');

    getSupplierCatalogueFit({
      sellerId: profileUserId,
      vehicle: catalogueVehicle(vehicle),
      diameter: diaDigits(diameter),
      tiretype: seasonTiretype,
    })
      .then(response => {
        if (!current) return;
        const next = {
          tyres: response?.tyres || [],
          rims: response?.rims || [],
          basis: response?.basis || null,
          etFiltered: !!response?.etFiltered,
          hasCatalogue: !!response?.hasCatalogue,
        };
        setCatalogue(next);
        setSelTyre(previous => keepOrFirst(next.tyres, previous));
        setSelRim(previous => keepOrFirst(next.rims, previous));
        setStatus('ready');
      })
      .catch(() => {
        if (!current) return;
        /* A dealer with no catalogue and an unreachable catalogue are shown
         * differently: the first is a fact about the shop, the second is our
         * problem, and telling a shopper "inget lager" when the server is down
         * is a lie. */
        setCatalogue(EMPTY_CATALOGUE);
        setStatus('error');
      });

    return () => {
      current = false;
    };
  }, [profileUserId, vehicle, diameter, seasonTiretype]);

  // On a successful plate lookup, replace the example with the real car
  // and snap the dimension toggle to a diameter the car actually supports.
  const handlePlateInfo = info => {
    const v = extractVehicle(info, regNum);
    if (!v) return;
    setVehicle(v);
    setCarShown(true);
    const dims = v.diameters || [];
    const current = diaDigits(diameter);
    if (dims.length && !dims.includes(current)) {
      setDiameter(`${dims[dims.length - 1]}"`);
    }
  };

  /* The size to print under a product. The PRODUCT's own size when there is a
   * product — a 19" rim must not be captioned with the vehicle's 21" spec —
   * and the vehicle-derived line only as the caption for an empty slot. */
  const vehicleTyreSize = tyreSizeForDiameter(vehicle, diameter) || `…R${diaDigits(diameter)}`;
  const vehicleRimSpec = rimSpecForVehicle(vehicle, diameter);
  const tyreSize = selTyre?.size || vehicleTyreSize;
  const rimSpec = selRim?.size || vehicleRimSpec;

  /* Per wheel. Every product price from the catalogue is per piece, which is
   * what makes the ×4 below correct — and why the catalogue refuses to store
   * wheel packages, which are priced per set of four. */
  const { perWheel, recycling } = useMemo(() => {
    const tyre = showTyre ? selTyre : null;
    const rim = showRim ? selRim : null;
    /* The supplier's own recycling fee, per piece, for what is selected. Shown
     * as its own line below, and the reason the module no longer adds a flat
     * fee of its own on top of a price that already contained one. */
    const fee = (tyre?.recyclingFee || 0) + (rim?.recyclingFee || 0);
    const products = (tyre?.price || 0) + (rim?.price || 0);
    return { perWheel: products + fee + MONT + VENTIL + (tpms ? TPMS : 0), recycling: fee };
  }, [showTyre, showRim, selTyre, selRim, tpms]);

  const grand = perWheel * SET;

  /**
   * Is there actually something to price?
   *
   * Every part the buyer asked for has to be there. Without this the summary
   * priced things that do not exist: a dealer with no catalogue at all showed
   * "Totalt 4 st 1 316 kr" — four times the workshop's own fees, for nothing —
   * and a wheel build with a tyre but no fitting rim priced the tyre as though
   * it were a complete wheel.
   *
   * The fees are per wheel, so they only mean anything once there is a wheel.
   */
  const buildComplete = (!showTyre || !!selTyre) && (!showRim || !!selRim);

  const onManageDisableScrolling = (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling));

  const pickerData = picking === 'tire' ? catalogue.tyres : catalogue.rims;
  const pickerBrands = picking ? [...new Set(pickerData.map(d => d.brand).filter(Boolean))] : [];
  const pickerShown = brandFilter ? pickerData.filter(d => d.brand === brandFilter) : pickerData;
  const pickerSpec = picking === 'tire' ? vehicleTyreSize : vehicleRimSpec;

  const openPicker = kind => {
    setBrandFilter(null);
    setPicking(kind);
  };

  const choose = item => {
    if (picking === 'tire') setSelTyre(item);
    else setSelRim(item);
    setPicking(null);
  };

  const seller = displayName || 'butiken';
  const carShort = [vehicle.brand, vehicle.model].filter(Boolean).join(' ') || vehicle.name;
  const dims = vehicle.diameters && vehicle.diameters.length ? vehicle.diameters : ['19', '20', '21', '22'];
  const diaRange =
    dims.length > 1 ? `${dims[0]}" – ${dims[dims.length - 1]}"` : `${dims[0] || '–'}"`;

  const builderClass = classNames(css.builder, {
    [css.builderSingle]: !(showTyre && showRim),
  });

  /* One sentence about the state of the catalogue, or '' when both slots are
   * filled and there is nothing to say. Five situations that a single "inget
   * hittades" would flatten into one — and naming which HALF is empty matters:
   * with rims found and tyres not, "nothing fits your car" is simply false. */
  const catalogueNote = () => {
    if (status === 'loading') return `Hämtar ${seller}s lager …`;
    if (status === 'error') return 'Lagret kunde inte läsas just nu. Försök igen om en stund.';
    if (status !== 'ready') return '';

    // A dealer with no catalogue loaded at all. Not a fact about this car.
    if (!catalogue.hasCatalogue) {
      return `${seller} har inte lagt upp något lager här ännu.`;
    }

    const missing = [
      showTyre && catalogue.tyres.length === 0 ? 'däck' : null,
      showRim && catalogue.rims.length === 0 ? 'fälgar' : null,
    ].filter(Boolean);
    if (missing.length === 0) return '';

    return (
      `Inga ${missing.join(' eller ')} i ${seller}s lager passar ${carShort} i ` +
      `${diaDigits(diameter)}"${showTyre && missing.includes('däck') ? ` (${season.toLowerCase()})` : ''}.`
    );
  };
  const note = catalogueNote();

  /* A product card with nothing in it. Kept as one function so the tyre and the
   * rim slot cannot drift apart. */
  const renderPick = ({ kind, title, product, image, spec, showEuLabel }) => (
    <div className={css.pick}>
      <div className={css.pickTitle}>{title}</div>
      <div className={css.prodImg}>
        <img src={product?.imageUrl || image} alt={kind === 'tire' ? 'däck' : 'fälg'} />
      </div>
      <div className={css.prodName}>
        {product ? [product.brand, product.name].filter(Boolean).join(' ') : '—'}
      </div>
      <div className={css.prodSub}>{spec}</div>
      {showEuLabel ? (
        <div
          className={classNames(css.euLabel, {
            [css.euLabelHidden]: !euLabelText(product?.euLabel),
          })}
        >
          {euLabelText(product?.euLabel) || '.'}
        </div>
      ) : (
        <div className={classNames(css.euLabel, css.euLabelHidden)}>.</div>
      )}
      <div className={css.price}>
        {product ? (
          <>
            {fmt(product.price)} kr<small>/st</small>
          </>
        ) : (
          <>–</>
        )}
      </div>
      <button
        type="button"
        className={css.btnChange}
        onClick={() => openPicker(kind)}
        disabled={!product}
      >
        {kind === 'tire' ? 'Byt däck' : 'Byt fälg'}
      </button>
    </div>
  );

  return (
    /* The CTA on every package page deep-links here (P25009-406 AC-2), so this
     * anchor is part of a URL that ships in listings — do not rename it. */
    <section id="bygg-ditt-hjul">
      <div className={css.secHead}>
        <h2>Bygg ditt hjul</h2>
        <span className={css.pillNew}>NYTT</span>
        <span className={css.secHeadHint}>— hos {seller}, monterat och klart</span>
      </div>

      <div className={css.build}>
        <div className={css.buildTop}>
          <h3>Slå ditt registreringsnummer så visar vi fälgar och däck som passar</h3>
          <div className={css.subtle}>
            Välj en fälg och ett däck ur butikens lager. Vi matchar mot din bil automatiskt.
          </div>

          {/* Same reg-lookup component as the /annonser page (RegNumInput,
           * compact). page !== 'searchPage' so it does NOT redirect — it
           * calls updateState here, which feeds the vehicle box below. */}
          <div className={css.regRow}>
            <div className={css.regInputWrap}>
              <RegNumInput
                page="companyProfile"
                listingType="wheel"
                size="compact"
                savedRegNum={regNum}
                changeRegNum={setRegNum}
                updateState={handlePlateInfo}
              />
            </div>
            {carShown ? (
              <button type="button" className={css.changeCar} onClick={() => setCarShown(false)}>
                Byt bilmodell
              </button>
            ) : null}
          </div>

          {carShown ? (
            <div className={css.dinbil}>
              {/* P25009-443 BR-14/AC-8: on a phone the vehicle facts collapse
                * behind a header that PRINTS its value (the car), so nothing
                * set is ever hidden — the BR-8 rule. The season/dimension
                * controls below stay outside the collapse: they are active
                * choices, and actions are never collapsed. Desktop never
                * sees this toggle. */}
              <button
                type="button"
                className={css.carPanelToggle}
                onClick={() => setCarPanelOpen(open => !open)}
                aria-expanded={carPanelOpen}
              >
                <span className={css.carPanelToggleName}>{vehicle.name}</span>
                {vehicle.staggered ? (
                  <span className={css.carPanelToggleHint}>staggered</span>
                ) : null}
                <span className={css.carPanelToggleChevron} aria-hidden="true">
                  {carPanelOpen ? '⌃' : '⌄'}
                </span>
              </button>

              <div className={classNames(css.carFacts, { [css.carFactsClosed]: !carPanelOpen })}>
              <div className={css.car}>
                <div>
                  <span className={css.subtle}>
                    DITT FORDON{vehicle.reg ? ` (${vehicle.reg})` : ''}
                    {vehicle.isExample ? ' · exempel' : ''}
                  </span>
                  <b>{vehicle.name}</b>
                </div>
                {vehicle.staggered ? (
                  <span className={classNames(css.chip)}>Staggered fram/bak</span>
                ) : null}
                {vehicle.tpmsText ? (
                  <span className={classNames(css.chip)} data-testid="tpms-chip">
                    {vehicle.tpmsText}
                  </span>
                ) : null}
              </div>

              <div className={css.specrow}>
                <div className={css.spec}>
                  <b>{vehicle.bolts && vehicle.pcd ? `${vehicle.bolts}×${vehicle.pcd}` : '–'}</b>
                  <span>Bultmönster</span>
                </div>
                <div className={css.spec}>
                  <b>{vehicle.cb ? `${vehicle.cb} mm` : '–'}</b>
                  <span>Navhål / CB</span>
                </div>
                <div className={css.spec}>
                  <b>{vehicle.et ? `ET ${vehicle.et}` : '–'}</b>
                  <span>Offset</span>
                </div>
                <div className={css.spec}>
                  <b>{diaRange}</b>
                  <span>Godkänd diameter</span>
                </div>
              </div>

              <div className={css.chips}>
                {(vehicle.frontTires || []).map((t, i) => (
                  <span key={`f-${t}`} className={classNames(css.chip, css.chipFront)}>
                    {i === 0 ? `${vehicle.staggered ? 'FRAM ' : ''}${t}` : t}
                  </span>
                ))}
                {vehicle.staggered
                  ? (vehicle.rearTires || []).map((t, i) => (
                      <span key={`r-${t}`} className={classNames(css.chip, css.chipRear)}>
                        {i === 0 ? `BAK ${t}` : t}
                      </span>
                    ))
                  : null}
              </div>
              </div>

              <div className={css.optrow}>
                {showTyre ? (
                  <div>
                    <div className={css.lab}>TYP AV DÄCK</div>
                    <div className={css.toggleGroup}>
                      {SEASONS.map(s => (
                        <div
                          key={s.label}
                          className={classNames(css.tg, { [css.tgOn]: season === s.label })}
                          onClick={() => setSeason(s.label)}
                        >
                          {s.label}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div>
                  <div className={css.lab}>DIMENSION</div>
                  <div className={css.toggleGroup}>
                    {dims.map(d => {
                      const label = `${d}"`;
                      return (
                        <div
                          key={d}
                          className={classNames(css.tg, { [css.tgOn]: diameter === label })}
                          onClick={() => setDiameter(label)}
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Product-type toggle: Bygg hjul / Endast däck / Endast fälg */}
          <div className={css.productTypeRow}>
            <div className={css.lab}>VAD VILL DU BYGGA?</div>
            <div className={css.productToggle} role="tablist">
              {[
                { id: 'wheel', label: 'Bygg hjul' },
                { id: 'tire', label: 'Endast däck' },
                { id: 'rim', label: 'Endast fälg' },
              ].map(pt => (
                <button
                  key={pt.id}
                  type="button"
                  role="tab"
                  aria-selected={productType === pt.id}
                  className={classNames({ [css.productOn]: productType === pt.id })}
                  onClick={() => setProductType(pt.id)}
                >
                  {pt.label}
                </button>
              ))}
            </div>
          </div>

          {note ? <div className={css.subtle}>{note}</div> : null}
        </div>

        <div className={builderClass}>
          {showTyre
            ? renderPick({
                kind: 'tire',
                title: '🛞 Däck',
                product: selTyre,
                image: TYRE_SVG,
                spec: tyreSize,
                showEuLabel: true,
              })
            : null}

          {showRim
            ? renderPick({
                kind: 'rim',
                title: '⚙️ Fälg',
                product: selRim,
                image: RIM_SVG,
                spec: rimSpec,
                showEuLabel: false,
              })
            : null}

          <div className={css.summary}>
            <h4>Summering</h4>
            {showTyre && selTyre ? (
              <div className={css.li}>
                <div className={css.liName}>
                  {[selTyre.brand, selTyre.name].filter(Boolean).join(' ')}
                  <small>{tyreSize}</small>
                </div>
                <div className={css.liAmt}>{fmt(selTyre.price)} kr/st</div>
              </div>
            ) : null}
            {showRim && selRim ? (
              <div className={css.li}>
                <div className={css.liName}>
                  {[selRim.brand, selRim.name].filter(Boolean).join(' ')}
                  <small>{rimSpec}</small>
                </div>
                <div className={css.liAmt}>{fmt(selRim.price)} kr/st</div>
              </div>
            ) : null}
            {/* The fees and the total are per WHEEL, so they only mean
              * something once there is a wheel to fit. Shown only for a
              * complete build — otherwise the panel priced things that do not
              * exist. */}
            {buildComplete ? (
              <>
                <div className={css.li}>
                  <div className={css.liName}>Miljöavgift</div>
                  <div className={css.liAmt}>{fmt(recycling)} kr/st</div>
                </div>
                <div className={css.li}>
                  <div className={css.liName}>Montering, balansering</div>
                  <div className={css.liAmt}>{MONT} kr/st</div>
                </div>
                <div className={css.li}>
                  <div className={css.liName}>Ventilbyte</div>
                  <div className={css.liAmt}>{VENTIL} kr/st</div>
                </div>
                <div className={classNames(css.li, css.liNoBorder)}>
                  <label className={css.addon}>
                    <input
                      type="checkbox"
                      checked={tpms}
                      onChange={e => setTpms(e.target.checked)}
                    />
                    TPMS-sensorer
                  </label>
                  <div className={css.liAmt}>{TPMS} kr/st</div>
                </div>
                <div className={css.total}>
                  <div className={css.subtle}>Totalt {SET} st</div>
                  <div className={css.totalBig}>{fmt(grand)} kr</div>
                </div>
              </>
            ) : (
              <div className={css.li}>
                <div className={css.liName}>
                  {showTyre && showRim && (selTyre || selRim)
                    ? 'Välj både däck och fälg för att se priset.'
                    : 'Inget att prissätta ännu.'}
                </div>
              </div>
            )}
            {/* Phase 2. A catalogue row is not a listing and Sharetribe
              * transacts against listings, so there is nothing to add to a
              * cart yet. Disabled and labelled rather than left looking live:
              * a button that silently does nothing is worse than one that
              * says why. */}
            <button type="button" className={css.btnCart} disabled>
              Kontakta butiken för att beställa
            </button>
            <div className={css.fitnote}>
              {selTyre || selRim
                ? `✔ Passar din ${carShort}${
                    catalogue.etFiltered ? ' · offset kontrollerad' : ''
                  }`
                : `Slå ditt registreringsnummer för att se vad som passar din bil.`}
            </div>
            {vehicle.staggered && (selTyre || selRim) ? (
              /* The builder prices four identical wheels. A staggered car takes
               * two sizes, so four of one is not a complete set for it — said
               * plainly rather than left for the buyer to discover. Matching
               * front and rear as a pair is what Bygg paket does, and wiring
               * that here belongs with the checkout work. */
              <div className={css.fitnote}>
                Din bil har olika dimensioner fram och bak. Priset gäller fyra likadana hjul —
                kontakta butiken för en staggered-sats.
              </div>
            ) : null}
            {selTyre?.orderOnly || selRim?.orderOnly ? (
              <div className={css.fitnote}>
                Beställningsvara
                {selTyre?.deliveryDays || selRim?.deliveryDays
                  ? ` · ca ${selTyre?.deliveryDays || selRim?.deliveryDays} dagars leveranstid`
                  : ''}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Modal
        id="BuildAWheelSection.picker"
        isOpen={!!picking}
        onClose={() => setPicking(null)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalContent}>
          <h3 className={css.modalTitle}>{picking === 'tire' ? 'Välj däck' : 'Välj fälg'}</h3>
          <p className={css.modalSub}>
            {picking === 'tire'
              ? `Passar ${carShort} · ${pickerSpec} · godkänd dimension`
              : `Passar ${carShort} · ${pickerSpec}`}
          </p>
          {/* Brand chips, and only where there are real brands to offer.
            *
            * Tyres have them: the feed states 64 manufacturers (Michelin,
            * Continental, Goodyear …) and they filter usefully.
            *
            * Rims do NOT, and that is a fact about the data rather than an
            * omission. The supplier's brand column on a rim holds its own
            * wholesale grouping — "AFL", "Rautamo", "OCL", "Specialfälgar" —
            * while the real maker sits at the front of the model name ("GMP
            * Stellar", "DEZENT TZ"). Grouping by that first word was measured
            * on the live catalogue and gives 11 chips for 28 rims, mixing
            * makers with model codes (TN16, AR1, R-SERIES), so it would label
            * chips with things that are not brands. Better no filter than a
            * misleading one; the picker is short enough to scan. */}
          {pickerBrands.length > 0 ? (
            <div className={css.brands}>
              {pickerBrands.map(b => (
                <span
                  key={b}
                  className={classNames(css.tg, { [css.tgOn]: brandFilter === b })}
                  onClick={() => setBrandFilter(brandFilter === b ? null : b)}
                >
                  {b}
                </span>
              ))}
              {brandFilter ? (
                <span className={css.tg} onClick={() => setBrandFilter(null)}>
                  Alla märken
                </span>
              ) : null}
            </div>
          ) : null}
          <div className={css.pgrid}>
            {pickerShown.map(d => (
              <div key={d.ref} className={css.pc} onClick={() => choose(d)}>
                <div className={css.pcImg}>
                  <img src={d.imageUrl || (picking === 'tire' ? TYRE_SVG : RIM_SVG)} alt="" />
                </div>
                <div className={css.pcName}>{[d.brand, d.name].filter(Boolean).join(' ')}</div>
                <div className={css.pcSub}>{d.size || pickerSpec}</div>
                <div className={css.pcPrice}>{fmt(d.price)} kr/st</div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default BuildAWheelSection;
